// MAD001-F0236 (owner directive 2026-09-14) — the person side drawer on
// /app/admin/realm-authority/groups: clicking a person's NAME in a group's
// member list opens this drawer beside the editor; the pen beside the name
// makes the name editable inline; Enter commits (Esc cancels). On commit the
// name is dispatched to RealmAuthority as ONE rename command (Core resolves
// it synchronously from the app's ack, same 3s SLA semantics as a membership
// save); the ack's personSnapshot is what this drawer renders, and the
// caller's onReload() re-fetch is what moves the picker and member tables —
// this component never fabricates an updated mirror from what the browser
// asked for.
//
// Outcome discipline (identical to the membership editor's):
// - the response is projected by projectPersonRenameResult's closed shapes;
//   an unverifiable response is an explicit error, never a shown success;
// - a refused/failed outcome surfaces RealmAuthority's (or Core's) own
//   message VERBATIM and leaves the drawer editable — the operator can
//   correct and retry without reopening;
// - a 401 carrying a step-up challenge is rethrown through performWrite so
//   the page's StepUpDialog machinery parks and replays the write (F0308);
// - an empty trimmed name is rejected in-drawer before any call; Esc never
//   calls the API;
// - the group editor's staged-draft semantics are untouched: the drawer owns
//   no draft state of the editor's.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getStepUpChallengeFromError } from '../../../utils/stepUp';
import { realmAuthorityGroupsApi } from '../../../services/realmAuthorityGroupsApi';
import { projectPersonRenameResult } from './normalisers';

function PenIcon() {
  return (
    <svg className="ra-person-pen-icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
        fill="currentColor"
      />
    </svg>
  );
}

export default function PersonDrawer({
  organisationId, person, groups, performWrite, onReload, onClose,
}) {
  const [name, setName] = useState(person.displayName || person.upn || person.objectSid);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Group memberships of this person across THIS organisation's groups view
  // (the mirror is the authority; the drawer computes, never caches).
  const memberships = useMemo(() => (groups || [])
    .filter((group) => (group.members || []).some((member) => member.objectSid === person.objectSid))
    .map((group) => group.groupName), [groups, person.objectSid]);

  // A different person opened, or the mirror refreshed under the drawer:
  // re-sync every local state to the prop. A background re-fetch while the
  // operator is mid-edit closes the editor deliberately — the name may have
  // changed under them and the mirror is authoritative.
  useEffect(() => {
    setName(person.displayName || person.upn || person.objectSid);
    setEditing(false);
    setDraft('');
    setSaving(false);
    setError(null);
  }, [person.objectSid, person.displayName, person.upn]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEditing = () => {
    setError(null);
    setDraft(name);
    setEditing(true);
  };

  const cancelEditing = () => {
    if (saving) return;
    setEditing(false);
    setDraft('');
    setError(null);
  };

  const commit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const trimmed = draft.trim();
    // In-drawer rejection BEFORE any call: a blank name is never sent.
    if (!trimmed) {
      setError({ message: 'Display name is required.' });
      return;
    }
    if (trimmed === name) {
      cancelEditing();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await performWrite(async () => {
        try {
          const response = await realmAuthorityGroupsApi.renamePerson(organisationId, person.objectSid, trimmed);
          const result = projectPersonRenameResult(response);
          if (!result) {
            setError({
              message: 'The response from RealmAuthority could not be verified. Nothing is shown as applied.',
            });
            return; // stays editable
          }
          if (result.outcome === 'applied') {
            // The ack's personSnapshot IS the new name — render it now; the
            // caller's re-fetch moves the picker and member tables.
            setName(result.person.displayName);
            setEditing(false);
            setDraft('');
            onReload?.();
            return;
          }
          // refused | failed: RealmAuthority's/Core's own words, verbatim.
          setError({ message: result.message });
        } catch (writeError) {
          if (getStepUpChallengeFromError(writeError)?.id) throw writeError;
          setError({
            message: writeError?.response?.data?.message
              || writeError?.message
              || 'RealmAuthority did not complete this change.',
          });
        }
      });
    } catch (stepUpError) {
      // A step-up challenge: the page's StepUpDialog takes over and replays
      // the write; the drawer stays open and editable meanwhile.
      setError(null);
      void stepUpError;
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside
      className="ra-person-drawer"
      data-testid="person-drawer"
      role="dialog"
      aria-label={`Person details — ${name}`}
    >
      <header className="ra-person-drawer-header">
        <div className="ra-person-drawer-title">
          {editing ? (
            <form className="ra-person-rename-form" onSubmit={commit}>
              <input
                ref={inputRef}
                type="text"
                className="ra-person-name-input"
                data-testid="person-name-input"
                value={draft}
                maxLength={128}
                disabled={saving}
                aria-label="Display name"
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.stopPropagation();
                    cancelEditing();
                  }
                }}
              />
            </form>
          ) : (
            <>
              <h3 className="ra-person-name" data-testid="person-name">{name}</h3>
              <button
                type="button"
                className="ra-person-pen"
                data-testid="person-name-edit"
                aria-label={`Edit display name of ${name}`}
                title="Edit display name (Enter to save, Esc to cancel)"
                onClick={startEditing}
              >
                <PenIcon />
              </button>
            </>
          )}
          <p className="ra-person-upn">{person.upn}</p>
        </div>
        <button
          type="button"
          className="ra-person-drawer-close"
          data-testid="person-drawer-close"
          aria-label="Close person details"
          onClick={onClose}
        >
          ×
        </button>
      </header>

      {error ? (
        <p className="ra-person-rename-error" role="alert" data-testid="person-rename-error">
          {error.message}
        </p>
      ) : null}

      <dl className="ra-person-facts">
        <div className="ra-person-fact">
          <dt>UPN</dt>
          <dd data-testid="person-drawer-upn">{person.upn}</dd>
        </div>
        {person.samAccountName ? (
          <div className="ra-person-fact">
            <dt>Account</dt>
            <dd data-testid="person-drawer-sam">{person.samAccountName}</dd>
          </div>
        ) : null}
        <div className="ra-person-fact">
          <dt>Member of</dt>
          <dd data-testid="person-drawer-memberships">
            {memberships.length ? (
              <ul className="ra-person-memberships">
                {memberships.map((groupName) => (
                  <li key={groupName}>{groupName}</li>
                ))}
              </ul>
            ) : 'No groups in this organisation'}
          </dd>
        </div>
      </dl>

      <p className="ra-person-drawer-help">
        Renaming changes the person&apos;s display name in the directory —
        Enter saves, Esc cancels.
      </p>
    </aside>
  );
}
